import {
  Button,
  Header,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { orgFromFirestore } from '@parallel-fluidics/shared/models/organizations';
import AddOrganizationModal from 'features/organization/components/addOrganizationModal';
import {
  collection,
  getFirestore,
  orderBy,
  where,
} from 'firebase/firestore';
import useCustomInfiniteQuery from 'hooks/use-customInfiniteQuery';
import useDebouncedValue from 'hooks/useDebouncedValue';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const [filterText, setFilterText] = useState('');
  const [editOrg, setEditOrg] = useState(null);
  const [openOrgModal, setOpenOrgModal] = useState(false);

  const debouncedFilterText = useDebouncedValue(filterText);

  const orgsQueryParams = useMemo(() => {
    const base = collection(getFirestore(), 'organizations');
    if (!debouncedFilterText) {
      return [
        base,
        orderBy('name_lowercase', 'asc'),
      ];
    }

    const term = debouncedFilterText.trim().toLowerCase();
    return [
      base,
      orderBy('name_lowercase', 'asc'),
      where('name_lowercase', '>=', term),
      where('name_lowercase', '<=', `${term}\uf8ff`),
    ];
  }, [debouncedFilterText]);

  const {
    CustomPagination,
    pageData,
    isFetchingNextPage,
    refetch,
  } = useCustomInfiniteQuery({
    queryKey: 'getOrganizationsData',
    queryParams: orgsQueryParams,
    pageSize: 25,
    transformData: orgFromFirestore,
  });

  const tableItems = useMemo(() => pageData.map((item) => ({
    ...item,
    nameButton: <Button variant="inline-link" onClick={() => navigate(`/organization/${item.id}`)}>{item.name}</Button>,
    editButton: <Button onClick={() => { setEditOrg(item); setOpenOrgModal(true); }}>Edit</Button>,
  })), [navigate, pageData]);

  return (
    <>
      <Table
        header={(
          <Header
            variant="awsui-h1-sticky"
            actions={(
              <Button
                onClick={() => setOpenOrgModal(true)}
                iconName="add-plus"
                variant="primary"
              >
                Create organization
              </Button>
            )}
          >
            Organizations
          </Header>
      )}
        filter={(
          <TextFilter
            filteringPlaceholder="Find organizations by name"
            filteringText={filterText}
            onChange={(e) => setFilterText(e.detail.filteringText)}
          />
      )}
        variant="full-page"
        loading={isFetchingNextPage}
        items={tableItems}
        stickyHeader
        resizableColumns
        columnDefinitions={[
          {
            id: 'name',
            header: 'Organization Name',
            cell: (item) => item.nameButton,
          },
          {
            id: 'domains',
            header: 'Domains',
            cell: (item) => item?.domains?.join(', '),
          },
          {
            id: 'note',
            header: 'Notes',
            cell: (item) => item.notes || '-',
          },
          {
            id: 'createDate',
            header: 'Create Date',
            cell: (item) => item.createDate?.toDate().toLocaleDateString(),
          },
          {
            id: 'edit',
            header: 'Actions',
            cell: (item) => item.editButton,
          },
        ]}
        pagination={<CustomPagination />}
        empty={<div>No organizations</div>}
      />
      {openOrgModal && (
        <AddOrganizationModal
          open={openOrgModal}
          onClose={() => {
            setEditOrg(null);
            setOpenOrgModal(false);
          }}
          onSuccess={() => {
            refetch();
          }}
          isEditing={Boolean(editOrg)}
          initOrg={editOrg}
        />
      )}
    </>
  );
}
